import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  FileQuestion,
  Link2,
  Lock,
  Paperclip,
  Star,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useParams } from "react-router";

import { useAuth } from "@/app/auth/context";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { Alert, Badge } from "@/components/ui/Feedback";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/cn";
import { formatBytes, formatDateTime } from "@/lib/format";
import { useShortcuts } from "@/lib/keyboard";
import { meApi, ticketsApi, type Article, type TicketDetail, type TicketTrail } from "@/lib/tickets-api";

import { HtmlMessage } from "./HtmlMessage";

import { StateBadge } from "./TicketBits";

function Meta({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={cn("min-w-0", className)}>
      <dt className="text-xs text-ink-subtle">{label}</dt>
      <dd className="mt-0.5 text-sm break-words text-ink">{children}</dd>
    </div>
  );
}

function Attachments({ article }: { article: Article }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  if (article.attachments.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-2 border-t border-line px-5 py-3" aria-label={t("ticket.attachments")}>
      {article.attachments.map((attachment) => {
        const label = (
          <>
            <Paperclip className="size-3.5 shrink-0" aria-hidden="true" />
            <span className="min-w-0 truncate">{attachment.filename || t("ticket.unnamedFile")}</span>
            <span className="shrink-0 text-ink-subtle tabular-nums">{formatBytes(attachment.size)}</span>
          </>
        );
        const base =
          "inline-flex max-w-full items-center gap-1.5 rounded-control border border-line px-2.5 py-1.5 text-xs";
        return (
          <li key={attachment.id} className="max-w-full">
            {attachment.has_content && can("attachment.download") ? (
              <a
                href={ticketsApi.downloadUrl(attachment.id)}
                download
                className={cn(base, "text-ink hover:border-primary hover:text-primary")}
              >
                {label}
                <Download className="size-3.5 shrink-0" aria-hidden="true" />
              </a>
            ) : (
              <span
                className={cn(base, "text-ink-muted")}
                title={
                  attachment.has_content ? t("ticket.noDownloadPermission") : t("ticket.contentNotImported")
                }
              >
                {label}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function ArticleCard({ article, open, onToggle }: { article: Article; open: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  // Formatted (HTML) when the message has it; the plain text stays one click away.
  const [view, setView] = useState<"html" | "text">(article.has_html ? "html" : "text");
  const issues = article.text_issues?.split(",") ?? [];
  const bodyId = `article-${article.id}`;
  return (
    <article
      className={cn(
        "rounded-card border bg-surface shadow-card",
        article.is_internal ? "border-accent/50 bg-accent-soft/30" : "border-line",
      )}
      aria-labelledby={`${bodyId}-title`}
    >
      <header className="flex items-start gap-3 px-5 py-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span id={`${bodyId}-title`} className="font-medium break-all">
              {article.from_addr ||
                t(`ticket.sender.${article.sender_kind}`, { defaultValue: article.sender_kind })}
            </span>
            <Badge tone={article.sender_kind === "customer" ? "primary" : "neutral"}>
              {t(`ticket.sender.${article.sender_kind}`, { defaultValue: article.sender_kind })}
            </Badge>
            {article.is_internal && (
              <Badge tone="accent">
                <Lock className="mr-1 size-3" aria-hidden="true" />
                {t("ticket.internal")}
              </Badge>
            )}
          </div>
          <div className="mt-0.5 text-xs text-ink-subtle tabular-nums">
            {formatDateTime(article.created_at)}
          </div>
          {article.subject && (
            <div className="mt-1 text-sm break-words text-ink-muted">{article.subject}</div>
          )}
        </div>
        <Button
          variant="ghost"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={bodyId}
          aria-label={open ? t("ticket.collapse") : t("ticket.expand")}
          className="shrink-0 px-2"
        >
          <ChevronDown
            className={cn("size-4 transition-transform", open && "rotate-180")}
            aria-hidden="true"
          />
        </Button>
      </header>
      {open && (
        <div id={bodyId}>
          {(article.to_addrs || article.cc_addrs) && (
            <dl className="grid gap-1 border-t border-line px-5 py-2 text-xs text-ink-muted sm:grid-cols-[auto_1fr] sm:gap-x-3">
              {article.to_addrs && (
                <>
                  <dt className="text-ink-subtle">{t("ticket.to")}</dt>
                  <dd className="break-all">{article.to_addrs}</dd>
                </>
              )}
              {article.cc_addrs && (
                <>
                  <dt className="text-ink-subtle">{t("ticket.cc")}</dt>
                  <dd className="break-all">{article.cc_addrs}</dd>
                </>
              )}
            </dl>
          )}
          {issues.includes("lost_chars") && (
            <p className="border-t border-line px-5 py-2 text-xs text-ink-subtle">{t("ticket.lostChars")}</p>
          )}
          {issues.includes("repaired_from_raw") && (
            <p className="border-t border-line px-5 py-2 text-xs text-ink-subtle">{t("ticket.repaired")}</p>
          )}
          {article.has_html && (
            <div
              role="group"
              aria-label={t("ticket.viewAs")}
              className="flex gap-1 border-t border-line px-5 pt-3"
            >
              {(["html", "text"] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  aria-pressed={view === mode}
                  onClick={() => setView(mode)}
                  className={cn(
                    "rounded-control px-2.5 py-1 text-xs font-medium",
                    view === mode
                      ? "bg-primary-soft text-primary"
                      : "text-ink-muted hover:bg-sunken hover:text-ink",
                  )}
                >
                  {t(`ticket.views.${mode}`)}
                </button>
              ))}
            </div>
          )}
          {view === "html" ? (
            <HtmlMessage articleId={article.id} />
          ) : (
            <div
              className={cn(
                "px-5 py-4 text-sm leading-relaxed break-words whitespace-pre-wrap",
                !article.has_html && "border-t border-line",
              )}
            >
              {article.body_text.trim() || <span className="text-ink-subtle">{t("ticket.emptyBody")}</span>}
            </div>
          )}
          <Attachments article={article} />
        </div>
      )}
    </article>
  );
}

function Thread({ ticket }: { ticket: TicketDetail }) {
  const { t } = useTranslation();
  const [closed, setClosed] = useState<Set<number>>(() => new Set());
  const allOpen = closed.size === 0;
  return (
    <section aria-labelledby="thread-title" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="thread-title" className="text-base font-semibold">
          {t("ticket.thread", { count: ticket.articles.length })}
        </h2>
        <Button
          variant="ghost"
          onClick={() => setClosed(allOpen ? new Set(ticket.articles.map((a) => a.id)) : new Set())}
        >
          {allOpen ? t("ticket.collapseAll") : t("ticket.expandAll")}
        </Button>
      </div>
      {ticket.articles.map((article) => (
        <ArticleCard
          key={article.id}
          article={article}
          open={!closed.has(article.id)}
          onToggle={() => {
            const next = new Set(closed);
            if (next.has(article.id)) next.delete(article.id);
            else next.add(article.id);
            setClosed(next);
          }}
        />
      ))}
    </section>
  );
}

function History({ ticket }: { ticket: TicketDetail }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!ticket.history) return null;
  return (
    <Card aria-labelledby="history-title">
      <CardHeader
        title={<span id="history-title">{t("ticket.history", { count: ticket.history.length })}</span>}
        actions={
          <Button
            variant="ghost"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-controls="history-list"
          >
            {open ? t("ticket.hide") : t("ticket.show")}
          </Button>
        }
      />
      {open && (
        <ol id="history-list" className="divide-y divide-line">
          {ticket.history.map((entry) => (
            <li key={entry.id} className="flex flex-wrap gap-x-4 gap-y-0.5 px-5 py-2 text-sm">
              <span className="w-36 shrink-0 text-ink-subtle tabular-nums">
                {formatDateTime(entry.created_at)}
              </span>
              <span className="min-w-0 break-words">
                <span className="font-medium">{entry.event_type}</span>
                {entry.raw_name && <span className="text-ink-muted"> — {entry.raw_name}</span>}
              </span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

export function TicketPage() {
  const { t } = useTranslation();
  const { number = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const ticket = useQuery({ queryKey: ["ticket", number], queryFn: () => ticketsApi.ticket(number) });
  const favorites = useQuery({ queryKey: ["me", "favorites"], queryFn: meApi.favorites });
  const isFavorite = favorites.data?.some((f) => f.number === number) ?? false;
  const favorite = useMutation({
    mutationFn: () => (isFavorite ? meApi.removeFavorite(number) : meApi.addFavorite(number)),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["me", "favorites"] }),
  });
  const [copied, setCopied] = useState(false);
  const copyLink = () => {
    const url = `${window.location.origin}/tickets/${encodeURIComponent(number)}`;
    void navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  };

  // Back to the list or the search results as they were (filters, page), when the user came from there;
  // previous/next walk through the tickets of that list.
  const trail = (useLocation().state as TicketTrail | null) ?? {};
  const from = trail.from;
  const position = trail.numbers?.indexOf(number) ?? -1;
  const previous = position > 0 ? trail.numbers?.[position - 1] : undefined;
  const next = position >= 0 ? trail.numbers?.[position + 1] : undefined;
  const open = (target: string | undefined) => {
    if (target) void navigate(`/tickets/${encodeURIComponent(target)}`, { state: trail });
  };
  useShortcuts({
    j: () => open(next),
    k: () => open(previous),
    u: () => void navigate(from ?? "/tickets"),
    s: () => favorite.mutate(),
  });
  const back = (
    <Link
      to={from ?? "/tickets"}
      className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink"
    >
      <ArrowLeft className="size-4" aria-hidden="true" />
      {from?.startsWith("/search") ? t("ticket.backToSearch") : t("ticket.back")}
    </Link>
  );

  if (ticket.error instanceof ApiError && ticket.error.status === 404) {
    return (
      <div className="space-y-4">
        {back}
        <Card>
          <EmptyState icon={FileQuestion} title={t("ticket.notFoundTitle")}>
            {t("ticket.notFoundBody", { number })}
          </EmptyState>
        </Card>
      </div>
    );
  }
  if (ticket.isError) return <Alert>{t("common.errorGeneric")}</Alert>;
  if (!ticket.data) return <p className="text-sm text-ink-muted">{t("common.loading")}</p>;

  const data = ticket.data;
  const customer = data.customer;
  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          {back}
          <div className="flex flex-wrap items-center gap-1">
            {trail.numbers && (
              <>
                <Button variant="ghost" disabled={!previous} onClick={() => open(previous)} title="k">
                  <ChevronLeft className="size-4" aria-hidden="true" />
                  {t("ticket.previous")}
                </Button>
                <Button variant="ghost" disabled={!next} onClick={() => open(next)} title="j">
                  {t("ticket.next")}
                  <ChevronRight className="size-4" aria-hidden="true" />
                </Button>
              </>
            )}
            <Button variant="ghost" onClick={() => favorite.mutate()} aria-pressed={isFavorite} title="s">
              <Star className={cn("size-4", isFavorite && "fill-accent text-accent")} aria-hidden="true" />
              {isFavorite ? t("ticket.unfavorite") : t("ticket.favorite")}
            </Button>
            <Button variant="ghost" onClick={copyLink}>
              {copied ? (
                <Check className="size-4" aria-hidden="true" />
              ) : (
                <Link2 className="size-4" aria-hidden="true" />
              )}
              {copied ? t("ticket.linkCopied") : t("ticket.copyLink")}
            </Button>
          </div>
        </div>
        <h1 className="text-[1.45rem] leading-tight font-semibold break-words">
          <span className="text-ink-muted tabular-nums">#{data.number}</span>{" "}
          {data.title || t("tickets.untitled")}
        </h1>
      </div>

      <Card aria-label={t("ticket.details")}>
        <CardBody>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-4 lg:grid-cols-4">
            <Meta label={t("ticket.state")}>
              <StateBadge name={data.state} category={data.state_category} />
            </Meta>
            <Meta label={t("ticket.queue")}>{data.queue}</Meta>
            <Meta label={t("ticket.priority")}>{data.priority || "—"}</Meta>
            <Meta label={t("ticket.owner")}>{data.owner || "—"}</Meta>
            <Meta label={t("ticket.created")}>{formatDateTime(data.created_at)}</Meta>
            <Meta label={t("ticket.changed")}>{formatDateTime(data.changed_at)}</Meta>
            <Meta label={t("ticket.closed")}>{data.closed_at ? formatDateTime(data.closed_at) : "—"}</Meta>
            <Meta label={t("ticket.customer")} className="col-span-2 lg:col-span-1">
              {customer ? (
                <>
                  {`${customer.first_name} ${customer.last_name}`.trim() || customer.login}
                  <span className="block text-xs text-ink-subtle">
                    {[customer.login, customer.customer_key].filter(Boolean).join(" · ")}
                  </span>
                  {customer.email && <span className="block break-all">{customer.email}</span>}
                  {customer.phone && <span className="block">{customer.phone}</span>}
                  {customer.mobile && <span className="block">{customer.mobile}</span>}
                </>
              ) : (
                data.customer_user || data.customer_key || "—"
              )}
            </Meta>
          </dl>
        </CardBody>
      </Card>

      <Thread ticket={data} />
      <History ticket={data} />
    </div>
  );
}
